import itemParser from './parsers/item';
import toError from './parsers/error';
import triggerProgressWhileTranscoding from './trigger-progress-while-transcoding';
import { excludedFields as itemExcludedFields } from './item';

import * as http from '../../utils/http';
import castArray from '../../utils/cast-aray';
import { keysToSnakeCase } from '@wix/wix-vod-shared/src/common/utils/object';
import { normalizeId } from '../../utils/normalize-id';
import TaskSequence from '../../utils/task-sequence';
import { parseCards } from '@wix/wix-vod-shared/src/common/parsers/cards/cards';

import Youtube from '../youtube/index';

import { listItems as externalListItemsRequests } from './external-requests';

const excludedFields = itemExcludedFields.concat(['itemType']);

const defaultPaging = {
  size: 30,
  cursor: null,
};

const defaultSort = {
  order: 'custom',
  direction: 'asc',
};

const youtubeAdapter = new Youtube();

const channelDataCache = {};

const parseActionsResponse = response => ({
  ...response,
  data: parseCards(response.data),
});

export default function (settings, adapterMethods) {

  const toItem = _.partial(itemParser, adapterMethods);


  function _optionsToParams(options) {
    const { query, tag, category, mediaType = settings.mediaType } = options;
    let { sort, paging } = options;
    sort = _.merge({}, defaultSort, sort);
    paging = _.merge({}, defaultPaging, paging);

    return {
      query: query || null,
      tag,
      category,
      media_type: mediaType,
      page_size: paging.size,
      cursor: paging.cursor,
      order: sort.direction === 'desc' ? `-${sort.order}` : sort.order,
    };
  }


  function _parseSuccessResponse(response) {
    const { data } = response;

    return _.assign({}, response, {
      data: toItem(data),
    });
  }


  function _parseListResponse(response, options) {
    const items = _.map(response.data.items, toItem);

    const { sort, paging } = options;

    return _.assign({}, response, {
      sort,
      paging: {
        size: paging.size,
        cursor: response.data.cursor || null,
      },
      data: items,
    });
  }


  function _fetchExternalVideos(channelData, options) {
    options = {
      ...options,
      noExtraChannelData: true,
    };

    const { externalId, channelType, urlType } = channelData;

    const key = `${channelType}.${urlType}`;
    const externalRequestMethod = externalListItemsRequests[key];

    if (!externalRequestMethod) {
      return {
        status: 200,
        data: {
          items: [],
        },
      };
    }

    return externalRequestMethod(externalId, options)
      .then(function (externalResponse) {
        return {
          status: 200,
          data: {
            cursor: externalResponse.paging.cursor,
            items: _.map(externalResponse.data, item => ({ ...item, id: item.externalId })),
          },
        };
      })
      .catch(function () {
        return {
          status: 200,
          data: {
            items: [],
          },
        };
      });
  }


  function _fetchRawList(trigger, listId, options = {}) {

    const { noAdditionalRequests = false } = options;
    const params = _optionsToParams(options);

    return http.get(`${settings.apiUrl}/public/lists/${listId}`, { params })
      .then(
        function (response) {
          if (noAdditionalRequests) {
            return response;
          }

          if (response.data.items.length) {
            return response;
          }

          return adapterMethods.list.getPublic(trigger, listId)
            .then(function (channelResponse) {
              const channelData = channelResponse.data;
              channelDataCache[listId] = channelData;

              return _fetchExternalVideos(channelData, options);
            });
        },

        function (reason) {
          if (noAdditionalRequests) {
            return Promise.reject(reason);
          }

          // invalid cursor means that it's external channel, e.g. youtube feed
          const isInvalidCursor = _(reason)
            .get('data.error_description')
            .startsWith('Invalid cursor');

          const channelData = channelDataCache[listId];

          if (isInvalidCursor && channelData) {
            return _fetchExternalVideos(channelData, options);
          }

          return Promise.reject(reason);
        },
      );
  }


  function _loadStartingFromVideoId(trigger, videoId, listId, options) {
    return _fetchRawList(trigger, listId, options)
      .then(function (response) {
        const indexOfVideo = videoId ?
          _.findIndex(response.data.items, item => (item.externalId || normalizeId(item.item_id)) === videoId) :
          0;

        if (indexOfVideo >= 0) {
          const firstPart = response.data.items.slice(0);
          firstPart.splice(indexOfVideo, 1);

          // videoId is in current response -> load more to reach paging.size and concatenate results.
          if (response.data.cursor) {
            return new Promise(function (resolve, reject) {
              const videoCountLeftToLoad = indexOfVideo + 1;

              const nextPageOptions = _.assign({}, options, {
                paging: {
                  size: videoCountLeftToLoad,
                  cursor: response.data.cursor,
                },
              });

              _fetchRawList(trigger, listId, nextPageOptions)
                .then(function (nextPageResponse) {
                  nextPageResponse.data.items = firstPart.concat(nextPageResponse.data.items);
                  resolve(nextPageResponse);
                })
                .catch(reject);
            });
          }

          response.data.items = firstPart;
          return response;
        }

        if (response.data.cursor) {
          const nextPageOptions = _.merge({}, options, {
            paging: {
              cursor: response.data.cursor,
            },
          });

          return _loadStartingFromVideoId(trigger, videoId, listId, nextPageOptions);
        }

        return _.assign({}, response, {
          data: { items: [] },
        });
      });
  }

  // TODO: move to listItem
  function get(trigger, listId, itemId) {
    return http.get(`${settings.apiUrl}/catalog/lists/${listId}/items/${itemId}`)
      .then(function (response) {
        return _.assign({}, response, {
          data: toItem(response.data),
        });
      })
      .then(function (parsedResponse) {
        triggerProgressWhileTranscoding([parsedResponse.data]);
        return parsedResponse;
      })
      .catch(toError);
  }


  function _tryGetYouTubeVideo(itemId) {
    return youtubeAdapter.videos.get(itemId)
      .then(function (response) {
        const videoItem = response.data[0];

        response = {
          ...response,
          data: {
            ...videoItem,
            id: videoItem.externalId,
          },
        };

        return response;
      });
  }

  function getPublic(trigger, listId, itemId) {
    return http.get(`${settings.apiUrl}/public/lists/${listId}/items/${itemId}/info`)
      .then(_parseSuccessResponse, () => _tryGetYouTubeVideo(itemId))
      .catch(toError);
  }

  function getActions(trigger, listId, itemId) {
    return http.get(`${settings.apiUrl}/public/lists/${listId}/items/${itemId}/actions`)
      .then(parseActionsResponse)
      .catch(toError);
  }


  function list(trigger, listId, options = {}) {
    const params = _optionsToParams(options);

    return http.get(`${settings.apiUrl}/catalog/lists/${listId}/items`, { params })
      .then(response => _parseListResponse(response, options))
      .then(function (response) {
        triggerProgressWhileTranscoding(response.data);
        return response;
      })
      .catch(toError);
  }


  function listPublic(trigger, listId, options = {}) {
    return _fetchRawList(trigger, listId, options)
      .then(response => _parseListResponse(response, options))
      .catch(toError);
  }


  function listNextInChannel(trigger, listId, options = {}) {
    const { videoId, paging } = options;

    if (paging && paging.cursor || !videoId) {
      return adapterMethods.listItems.listPublic(trigger, listId, options);
    }

    return _loadStartingFromVideoId(trigger, videoId, listId, options)
      .then(response => _parseListResponse(response, options))
      .catch(toError);
  }

  function add(trigger, listId, itemIds, data) {
    itemIds = _.compact(castArray(itemIds));

    if (_.isEmpty(itemIds)) {
      return toError({
        status: 500,
        data: {
          errorCode: 500,
          errorDescription: 'no item ids provided',
        },
      });
    }

    data = {
      ...keysToSnakeCase(data),
      items: itemIds,
    };

    return http.post(`${settings.apiUrl}/catalog/lists/${listId}/items`, { data })
      .then(function (response) {
        const items = _.map(response.data, toItem);
        triggerProgressWhileTranscoding(items);

        return _.assign({}, response, { data: items });
      })
      .catch(toError);
  }


  function update(trigger, listId, itemData) {
    itemData = _.assign({}, itemData);

    const { id, publishTs } = itemData;

    if (publishTs) {
      itemData.publishTs = Math.round(publishTs.valueOf() / 1000);
    }

    const data = keysToSnakeCase(_.omit(itemData, excludedFields));

    return http.put(`${settings.apiUrl}/catalog/lists/${listId}/items/${id}`, { data })
      .then(_parseSuccessResponse)
      .catch(toError);
  }


  function _performListItemsRemoveRequest(listId, data) {
    return http.post(`${settings.apiUrl}/catalog/lists/${listId}/items/delete`, { data })
      .catch(toError);
  }


  function remove(trigger, listId, itemIds) {
    const data = {
      items: castArray(itemIds),
    };

    return _performListItemsRemoveRequest(listId, data);
  }


  function removeByBaseItemId(trigger, listId, itemIds) {
    const data = {
      base_items: castArray(itemIds),
    };

    return _performListItemsRemoveRequest(listId, data);
  }

  const moveSequence = new TaskSequence();

  function move(trigger, listId, itemIds, moveData) {
    const data = {
      ...keysToSnakeCase(moveData),
      items: castArray(itemIds),
    };

    return moveSequence.runSequentially(function () {
      return http.post(`${settings.apiUrl}/catalog/lists/${listId}/items-move`, { data })
        .then(_parseSuccessResponse)
        .catch(toError);
    });
  }


  return {
    list,
    listPublic,
    listNextInChannel,
    add,
    get,
    getPublic,
    getActions,
    update,
    remove,
    removeByBaseItemId,
    move,
  };
}



// WEBPACK FOOTER //
// ./src/transport/adapters/vod/list-items.js